import React, { useState } from 'react';
import { useEnquiry } from '../../contexts/EnquiryContext';
import { 
  Mail, 
  Phone, 
  User, 
  Calendar, 
  MessageSquare, 
  Eye, 
  Trash2,
  Check,
  Filter,
  Search
} from 'lucide-react';

const Enquiries = () => {
  const { enquiries, markAsRead, markAsReplied, deleteEnquiry, addEnquiry } = useEnquiry();
  const [filter, setFilter] = useState('all'); // all, new, read, replied
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // DEBUG: Log enquiries on component mount and when they change
  React.useEffect(() => {
    console.log('=== ENQUIRIES PAGE DEBUG ===');
    console.log('Enquiries from context:', enquiries);
    console.log('Enquiries count:', enquiries.length);
    
    // Check localStorage directly
    const localStorageData = localStorage.getItem('contact_enquiries');
    console.log('localStorage raw data:', localStorageData);
    
    if (localStorageData) {
      try {
        const parsed = JSON.parse(localStorageData);
        console.log('localStorage parsed:', parsed);
        console.log('localStorage count:', parsed.length);
      } catch (e) {
        console.error('Error parsing localStorage:', e);
      }
    } else {
      console.warn('⚠️ No contact_enquiries in localStorage!');
    }
  }, [enquiries]);

  const filteredEnquiries = enquiries.filter(enq => {
    const matchesFilter = filter === 'all' || enq.status === filter;
    const matchesSearch = 
      enq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enq.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      enq.message.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleView = (enquiry) => {
    setSelectedEnquiry(enquiry);
    if (enquiry.status === 'new') {
      markAsRead(enquiry.id);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this enquiry?')) {
      deleteEnquiry(id);
      if (selectedEnquiry?.id === id) {
        setSelectedEnquiry(null);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'bg-green-100 text-green-800';
      case 'read': return 'bg-blue-100 text-blue-800';
      case 'replied': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const stats = {
    total: enquiries.length,
    new: enquiries.filter(e => e.status === 'new').length,
    read: enquiries.filter(e => e.status === 'read').length,
    replied: enquiries.filter(e => e.status === 'replied').length
  };

  // DEBUG: Add test enquiry button
  const handleAddTestEnquiry = () => {
    console.log('=== ADDING TEST ENQUIRY FROM ADMIN ===');
    const testData = {
      name: 'Test User ' + Date.now(),
      email: 'test@example.com',
      phone: '123456789',
      service: 'Web Development',
      message: 'This is a test enquiry added from admin panel'
    };
    
    try {
      const result = addEnquiry(testData);
      console.log('✅ Test enquiry added:', result);
      alert('Test enquiry added successfully! Check the list below.');
    } catch (e) {
      console.error('❌ Error adding test enquiry:', e);
      alert('Error: ' + e.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-primary mb-2">Contact Enquiries</h1>
            <p className="text-gray-600">Manage all contact form submissions</p>
          </div>
          <button
            onClick={handleAddTestEnquiry}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-md"
          >
            🧪 Add Test Enquiry
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Total Enquiries</p>
                <p className="text-4xl font-bold text-primary mt-2">{stats.total}</p>
              </div>
              <Mail className="w-12 h-12 text-primary opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">New</p>
                <p className="text-4xl font-bold text-green-600 mt-2">{stats.new}</p>
              </div>
              <MessageSquare className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Read</p>
                <p className="text-4xl font-bold text-blue-600 mt-2">{stats.read}</p>
              </div>
              <Eye className="w-12 h-12 text-blue-600 opacity-20" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-semibold">Replied</p>
                <p className="text-4xl font-bold text-gray-600 mt-2">{stats.replied}</p>
              </div>
              <Check className="w-12 h-12 text-gray-600 opacity-20" />
            </div>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
              {['all', 'new', 'read', 'replied'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    filter === status
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, email, or message..."
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Enquiries List & Detail View */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* List */}
          <div className="lg:col-span-1 bg-white rounded-xl shadow-md overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-primary-700 px-6 py-4">
              <h2 className="text-xl font-bold text-white">
                Enquiries ({filteredEnquiries.length})
              </h2>
            </div>

            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {filteredEnquiries.length === 0 ? (
                <div className="px-6 py-12 text-center text-gray-500">
                  No enquiries found
                </div>
              ) : (
                filteredEnquiries.map(enquiry => (
                  <div
                    key={enquiry.id}
                    onClick={() => handleView(enquiry)}
                    className={`px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedEnquiry?.id === enquiry.id ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{enquiry.name}</h3>
                        <p className="text-sm text-gray-600">{enquiry.email}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${getStatusColor(enquiry.status)}`}>
                        {enquiry.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 line-clamp-2 mb-2">{enquiry.message}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(enquiry.submittedAt).toLocaleString()}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Detail View */}
          <div className="lg:col-span-2">
            {selectedEnquiry ? (
              <div className="bg-white rounded-xl shadow-md">
                <div className="bg-gradient-to-r from-primary to-primary-700 px-6 py-4 flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Enquiry Details</h2>
                  <span className={`text-sm font-semibold px-3 py-1 rounded ${getStatusColor(selectedEnquiry.status)}`}>
                    {selectedEnquiry.status}
                  </span>
                </div>

                <div className="p-6 space-y-6">
                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                      <User className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-semibold text-gray-900">{selectedEnquiry.name}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                      <Mail className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="font-semibold text-gray-900">{selectedEnquiry.email}</p>
                      </div>
                    </div>

                    {selectedEnquiry.phone && (
                      <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                        <Phone className="w-5 h-5 text-primary" />
                        <div>
                          <p className="text-xs text-gray-500">Phone</p>
                          <p className="font-semibold text-gray-900">{selectedEnquiry.phone}</p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-3 bg-gray-50 p-4 rounded-lg">
                      <Calendar className="w-5 h-5 text-primary" />
                      <div>
                        <p className="text-xs text-gray-500">Submitted</p>
                        <p className="font-semibold text-gray-900">
                          {new Date(selectedEnquiry.submittedAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">Message</label>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-900 whitespace-pre-wrap">{selectedEnquiry.message}</p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <a
                      href={`mailto:${selectedEnquiry.email}`}
                      onClick={() => markAsReplied(selectedEnquiry.id)}
                      className="flex-1 bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors text-center"
                    >
                      Reply via Email
                    </a>
                    {selectedEnquiry.phone && (
                      <a
                        href={`tel:${selectedEnquiry.phone}`}
                        className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                      >
                        <Phone className="w-5 h-5" />
                        Call
                      </a>
                    )}
                    <button
                      onClick={() => handleDelete(selectedEnquiry.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
                    >
                      <Trash2 className="w-5 h-5" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-md p-12 text-center">
                <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select an enquiry to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Enquiries;
